import { useMemo, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import PageContainer from '@/components/ui/containers/PageContainer'

import PoliciesTable from './components/PoliciesTable'

import { useDeletePolicyMutation, useGetPoliciesQuery } from '@/core/services/policy'
import { Role } from '@/core/types/shared'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'

export function PoliciesPage() {
  const { pathname } = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const { data: policies } = useGetPoliciesQuery()
  const [deletePolicy, { isLoading: isDeletingPolicy }] = useDeletePolicyMutation()

  const policiesTableData = useMemo(
    () =>
      policies?.map((policy) => ({
        id: policy._id,
        name: policy.name,
        description: policy.description,
        canDelete: authUser?.roles.includes(Role.SuperAdmin) || policy.createdBy === authUser?._id,
      })) ?? [],
    [authUser, policies]
  )

  const [deletingPolicyId, setDeletingPolicyId] = useState<string>()
  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState(false)

  return (
    <PageContainer>
      <Stack spacing={4}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h2">{t('page.policies.title')}</Typography>
          <Stack direction="row">
            <Button component={Link} to={`${pathname}/create`} variant="contained" id="create-establishment-button">
              {t('common:button.create.label')}
            </Button>
          </Stack>
        </Stack>
        <PoliciesTable
          data={policiesTableData}
          onDeleteClick={(policyId) => {
            setDeletingPolicyId(policyId)
            setOpenDeleteConfirmationDialog(true)
          }}
          selectable={false}
        />
      </Stack>
      <DeleteConfirmationDialog
        isDeleting={isDeletingPolicy}
        maxWidth={800}
        onClose={() => {
          setOpenDeleteConfirmationDialog(false)
        }}
        onDelete={async () => {
          if (deletingPolicyId) {
            try {
              await deletePolicy(deletingPolicyId).unwrap()
            } catch (error) {
              enqueueSnackbar(getApiErrorMessage(error, t('notification.policy.delete.error')), { variant: 'error' })
            }
            setDeletingPolicyId(undefined)
            setOpenDeleteConfirmationDialog(false)
          }
        }}
        open={openDeleteConfirmationDialog}
        title={t('dialog.deleteConfirmation.policy.title')}
        message={t('dialog.deleteConfirmation.policy.message')}
      />
    </PageContainer>
  )
}

export default PoliciesPage
